/**
 * LYT Communications - Contractor Invoice
 * Version: 1.0.0
 * Created: 2026-02-23
 *
 * Self-service invoice creation for contractors.
 * Shows only QC-approved work at contractor rate (NEVER Vexus rate).
 * Contractor can add custom line items, submit for admin review.
 * 10% retainage auto-calculated on every invoice.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, Plus, Trash2, Send, Save, DollarSign, FileText, CheckCircle, Clock, X, AlertTriangle, Loader } from 'lucide-react';
import { loadFullProject, getContractorSegments, createInvoice, submitInvoice, loadContractorInvoices } from '../services/mapService';
import { VEXUS_RATES } from '../config/mapConfig';

function ContractorInvoice({ darkMode, user, setCurrentPage }) {
  const [segments, setSegments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState('list'); // 'list' or 'create'
  const [notification, setNotification] = useState(null);

  // Invoice form state
  const [selectedSegments, setSelectedSegments] = useState(new Set());
  const [customItems, setCustomItems] = useState([]);
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const accentPrimary = darkMode ? '#667eea' : '#0077B6';
  const accentSecondary = darkMode ? '#ff6b35' : '#28a745';
  const accentError = darkMode ? '#ff6b6b' : '#e85a4f';
  const bgColor = darkMode ? '#0d1b2a' : '#f8fafc';
  const cardBg = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await loadFullProject(null);
      if (data?.segments) {
        const companyName = user?.company || user?.name || '';
        const mySegs = getContractorSegments(data.segments, companyName);
        setSegments(mySegs);
      }
      const invs = await loadContractorInvoices(user?.email);
      setInvoices(invs);
    } catch (err) {
      console.error('[ContractorInvoice] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // QC approved segments that can be invoiced
  const invoiceableSegments = useMemo(() => {
    return segments.filter(s =>
      s.boring_status === 'QC Approved' ||
      s.pulling_status === 'QC Approved' ||
      (s.splicing_status && s.splicing_status === 'QC Approved')
    );
  }, [segments]);

  // Calculate line items from selected segments using contractor rates
  const segmentLineItems = useMemo(() => {
    const items = [];
    invoiceableSegments.forEach(seg => {
      if (!selectedSegments.has(seg.segment_id)) return;
      const footage = parseFloat(seg.footage) || 0;
      const actualFootage = parseFloat(seg.boring_actual_footage) || footage;

      if (seg.boring_status === 'QC Approved') {
        const rate = VEXUS_RATES?.UG1;
        const price = rate?.contractor_rate || 6.00;
        items.push({
          segment_id: seg.segment_id,
          code: 'UG1',
          description: `Boring — ${seg.street_name || seg.segment_id} (${actualFootage} LF)`,
          qty: actualFootage,
          uom: 'LF',
          rate: price,
          total: Math.round(actualFootage * price * 100) / 100,
        });
      }

      if (seg.pulling_status === 'QC Approved') {
        const cableType = seg.pulling_cable_type || '24F';
        const ct = parseInt(cableType) || 24;
        const code = ct > 144 ? 'UG28' : 'UG4';
        const rate = VEXUS_RATES?.[code];
        const price = rate?.contractor_rate || (ct > 144 ? 0.80 : 0.45);
        items.push({
          segment_id: seg.segment_id,
          code,
          description: `Pull ${cableType} — ${seg.street_name || seg.segment_id} (${footage} LF)`,
          qty: footage,
          uom: 'LF',
          rate: price,
          total: Math.round(footage * price * 100) / 100,
        });
      }
    });
    return items;
  }, [invoiceableSegments, selectedSegments]);

  const allLineItems = useMemo(() => {
    return [
      ...segmentLineItems,
      ...customItems.map((ci, idx) => ({
        ...ci,
        total: Math.round((parseFloat(ci.qty) || 0) * (parseFloat(ci.rate) || 0) * 100) / 100,
        _custom: true,
        _idx: idx,
      })),
    ];
  }, [segmentLineItems, customItems]);

  const subtotal = useMemo(() => allLineItems.reduce((sum, item) => sum + (item.total || 0), 0), [allLineItems]);
  const retainageAmount = Math.round(subtotal * 0.10 * 100) / 100;
  const netAmount = Math.round((subtotal - retainageAmount) * 100) / 100;

  const showNotification = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const addCustomItem = () => {
    setCustomItems(prev => [...prev, { code: '', description: '', qty: 1, uom: 'EA', rate: 0 }]);
  };

  const updateCustomItem = (idx, field, value) => {
    setCustomItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeCustomItem = (idx) => {
    setCustomItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = useCallback(async () => {
    if (allLineItems.length === 0) {
      showNotification('Add at least one line item', 'error');
      return;
    }
    setSaving(true);
    const lineItemsJson = JSON.stringify(allLineItems.map(li => ({
      code: li.code, description: li.description, qty: li.qty, uom: li.uom, rate: li.rate, total: li.total,
    })));

    const invoiceId = await createInvoice({
      project_id: segments[0]?.project_id || '',
      contractor_company: user?.company || user?.name || '',
      contractor_email: user?.email || '',
      invoice_number: invoiceNumber || `CINV-${Date.now()}`,
      line_items_json: lineItemsJson,
      subtotal: String(subtotal),
      notes: invoiceNotes,
    });

    if (invoiceId) {
      showNotification('Invoice saved as draft');
      await loadData();
      setActiveView('list');
      resetForm();
    } else {
      showNotification('Failed to save invoice', 'error');
    }
    setSaving(false);
  }, [allLineItems, invoiceNumber, invoiceNotes, subtotal, user, segments]);

  const handleSubmit = useCallback(async (invoiceId) => {
    const ok = await submitInvoice(invoiceId);
    if (ok) {
      // Notify admins of new contractor invoice (WORKFLOW Step 128)
      try {
        const { notifyContractorInvoiceSubmitted } = await import('../services/notificationService');
        const inv = invoices.find(i => i.invoice_id === invoiceId);
        await notifyContractorInvoiceSubmitted(
          inv?.invoice_number || invoiceId,
          user?.company || user?.name || 'Unknown',
          user?.email || '',
          inv?.net_amount || 0
        );
      } catch (err) { console.warn('[ContractorInvoice] Submit notification failed:', err); }

      showNotification('Invoice submitted for review');
      await loadData();
    }
  }, [invoices, user]);

  const resetForm = () => {
    setSelectedSegments(new Set());
    setCustomItems([]);
    setInvoiceNotes('');
    setInvoiceNumber('');
  };

  const toggleSegment = (segId) => {
    setSelectedSegments(prev => {
      const next = new Set(prev);
      if (next.has(segId)) next.delete(segId);
      else next.add(segId);
      return next;
    });
  };

  const inputStyle = {
    padding: '10px 12px', borderRadius: 8, border: `1px solid ${borderColor}`,
    background: darkMode ? '#0d1b2a' : '#fff', color: textColor,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  const formatCurrency = (amt) => `$${(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const STATUS_COLORS = {
    'Draft': '#9E9E9E',
    'Submitted': '#FFB800',
    'Approved': '#4CAF50',
    'Rejected': '#FF4444',
    'Paid': '#2196F3',
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={32} color={accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: '24px' }}>
      {notification && (
        <div style={{ position: 'fixed', top: 20, right: 20, padding: '14px 20px', borderRadius: 8, background: notification.type === 'error' ? accentError : accentSecondary, color: '#fff', zIndex: 1100, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {notification.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {notification.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => setCurrentPage('contractor-dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textColor, padding: 4 }}>
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: textColor, margin: 0 }}>My Invoices</h1>
          <p style={{ color: mutedColor, fontSize: '0.85rem', margin: 0 }}>{user?.company || user?.name}</p>
        </div>
        {activeView === 'list' && (
          <button
            onClick={() => setActiveView('create')}
            style={{ marginLeft: 'auto', padding: '10px 18px', background: accentPrimary, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={16} /> New Invoice
          </button>
        )}
        {activeView === 'create' && (
          <button
            onClick={() => { setActiveView('list'); resetForm(); }}
            style={{ marginLeft: 'auto', padding: '10px 18px', background: 'transparent', color: textColor, border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
          >
            Cancel
          </button>
        )}
      </div>

      {/* Invoice List View */}
      {activeView === 'list' && (
        <div>
          {invoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, background: cardBg, borderRadius: 12 }}>
              <FileText size={48} color={mutedColor} style={{ marginBottom: 12 }} />
              <p style={{ color: mutedColor, fontSize: '1rem' }}>No invoices yet. Create your first invoice from QC-approved work.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {invoices.map((inv, idx) => (
                <div key={idx} style={{ background: cardBg, borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: `${STATUS_COLORS[inv.status] || '#9E9E9E'}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DollarSign size={20} color={STATUS_COLORS[inv.status] || '#9E9E9E'} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: textColor }}>{inv.invoice_number || inv.invoice_id}</div>
                    <div style={{ fontSize: '0.8rem', color: mutedColor }}>{inv.project_id} — {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, color: textColor }}>{formatCurrency(parseFloat(inv.net_amount))}</div>
                    <span style={{
                      fontSize: '0.7rem', padding: '2px 8px', borderRadius: 8, fontWeight: 600,
                      background: `${STATUS_COLORS[inv.status] || '#9E9E9E'}20`,
                      color: STATUS_COLORS[inv.status] || '#9E9E9E',
                    }}>{inv.status}</span>
                  </div>
                  {inv.status === 'Draft' && (
                    <button
                      onClick={() => handleSubmit(inv.invoice_id)}
                      style={{ padding: '6px 12px', background: accentPrimary, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                    >
                      Submit
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {invoices.length > 0 && (
            <div style={{ marginTop: 20, background: cardBg, borderRadius: 12, padding: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {[
                { label: 'Total Invoiced', value: formatCurrency(invoices.reduce((s, i) => s + (parseFloat(i.subtotal) || 0), 0)), color: textColor },
                { label: 'Retainage Held', value: formatCurrency(invoices.filter(i => i.retainage_status === 'Held').reduce((s, i) => s + (parseFloat(i.retainage_amount) || 0), 0)), color: '#FFB800' },
                { label: 'Paid', value: formatCurrency(invoices.filter(i => i.status === 'Paid').reduce((s, i) => s + (parseFloat(i.paid_amount) || 0), 0)), color: '#4CAF50' },
                { label: 'Pending', value: formatCurrency(invoices.filter(i => i.status === 'Submitted').reduce((s, i) => s + (parseFloat(i.net_amount) || 0), 0)), color: '#FFB800' },
              ].map((stat, idx) => (
                <div key={idx}>
                  <div style={{ fontSize: '0.75rem', color: mutedColor }}>{stat.label}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Invoice View */}
      {activeView === 'create' && (
        <div>
          {/* Invoice number */}
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: mutedColor, display: 'block', marginBottom: 4 }}>Invoice Number</label>
            <input
              value={invoiceNumber}
              onChange={e => setInvoiceNumber(e.target.value)}
              placeholder="Auto-generated if blank"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>

          {/* Select QC-approved segments */}
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12 }}>
            <h3 style={{ color: textColor, fontSize: '1rem', fontWeight: 600, marginBottom: 8 }}>QC-Approved Work</h3>
            <p style={{ fontSize: 12, color: mutedColor, marginBottom: 12 }}>Select completed work to include on this invoice.</p>
            {invoiceableSegments.length === 0 ? (
              <p style={{ color: mutedColor, textAlign: 'center', padding: 16 }}>No QC-approved segments available.</p>
            ) : (
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {invoiceableSegments.map(seg => (
                  <label key={seg.segment_id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                    borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                    background: selectedSegments.has(seg.segment_id) ? `${accentPrimary}15` : 'transparent',
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedSegments.has(seg.segment_id)}
                      onChange={() => toggleSegment(seg.segment_id)}
                      style={{ width: 16, height: 16 }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 600, color: textColor, fontSize: 13 }}>{seg.segment_id}</span>
                      <span style={{ color: mutedColor, fontSize: 12, marginLeft: 8 }}>{seg.street_name || ''} — {seg.footage} LF</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {seg.boring_status === 'QC Approved' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, background: '#4CAF5020', color: '#4CAF50' }}>Boring</span>}
                      {seg.pulling_status === 'QC Approved' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, background: '#2196F320', color: '#2196F3' }}>Pulling</span>}
                    </div>
                  </label>
                ))}
              </div>
            )}
            {invoiceableSegments.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => setSelectedSegments(new Set(invoiceableSegments.map(s => s.segment_id)))} style={{ padding: '6px 12px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 6, background: 'transparent', color: textColor, cursor: 'pointer' }}>Select All</button>
                <button onClick={() => setSelectedSegments(new Set())} style={{ padding: '6px 12px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 6, background: 'transparent', color: textColor, cursor: 'pointer' }}>Clear All</button>
              </div>
            )}
          </div>

          {/* Custom Line Items */}
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h3 style={{ color: textColor, fontSize: '1rem', fontWeight: 600, margin: 0 }}>Additional Items</h3>
              <button onClick={addCustomItem} style={{ padding: '6px 12px', fontSize: 12, background: accentPrimary, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Plus size={14} /> Add Item
              </button>
            </div>
            {customItems.map((ci, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'flex-end' }}>
                <div style={{ flex: 2 }}>
                  {idx === 0 && <label style={{ fontSize: 10, color: mutedColor }}>Description</label>}
                  <input value={ci.description} onChange={e => updateCustomItem(idx, 'description', e.target.value)} placeholder="Description" style={{ ...inputStyle, width: '100%' }} />
                </div>
                <div style={{ width: 70 }}>
                  {idx === 0 && <label style={{ fontSize: 10, color: mutedColor }}>Qty</label>}
                  <input type="number" value={ci.qty} onChange={e => updateCustomItem(idx, 'qty', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
                </div>
                <div style={{ width: 80 }}>
                  {idx === 0 && <label style={{ fontSize: 10, color: mutedColor }}>Rate</label>}
                  <input type="number" value={ci.rate} onChange={e => updateCustomItem(idx, 'rate', e.target.value)} placeholder="0.00" style={{ ...inputStyle, width: '100%' }} />
                </div>
                <div style={{ width: 80, textAlign: 'right', fontSize: 13, fontWeight: 600, color: textColor, paddingBottom: 10 }}>
                  {formatCurrency((parseFloat(ci.qty) || 0) * (parseFloat(ci.rate) || 0))}
                </div>
                <button onClick={() => removeCustomItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: accentError, padding: 4, marginBottom: 6 }}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {customItems.length === 0 && (
              <p style={{ fontSize: 12, color: mutedColor, textAlign: 'center', padding: 8 }}>No additional items. Click "Add Item" to include extra work.</p>
            )}
          </div>

          {/* Notes */}
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: mutedColor, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea
              value={invoiceNotes}
              onChange={e => setInvoiceNotes(e.target.value)}
              placeholder="Any additional notes for this invoice..."
              rows={2}
              style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
            />
          </div>

          {/* Totals */}
          <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: mutedColor }}>Subtotal ({allLineItems.length} items)</span>
              <span style={{ fontWeight: 600, color: textColor }}>{formatCurrency(subtotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: '#FFB800' }}>Retainage (10%)</span>
              <span style={{ fontWeight: 600, color: '#FFB800' }}>-{formatCurrency(retainageAmount)}</span>
            </div>
            <div style={{ height: 1, background: borderColor, margin: '8px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, color: textColor, fontSize: '1.1rem' }}>Net Payable</span>
              <span style={{ fontWeight: 700, color: accentSecondary, fontSize: '1.1rem' }}>{formatCurrency(netAmount)}</span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={handleSave}
              disabled={saving || allLineItems.length === 0}
              style={{
                flex: 1, padding: 14, fontSize: '1rem', fontWeight: 600,
                background: allLineItems.length > 0 ? accentPrimary : '#9E9E9E',
                color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Save size={18} /> {saving ? 'Saving...' : 'Save Draft'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContractorInvoice;
